import i18n from '@/translations/i18n'
import {z} from 'zod'
import {
    CyclePeriodicityHypothesis,
    DisabilityExperienceProcessing,
    MedicalReportHouseType,
    MedicalReportJobType,
    MedicalReportQualification,
    MedicalReportSmall,
    MedicalReportWorkingCondition,
    PredefinedText,
    FamilySituation
} from '@/features/medical-reports/types'
import {ProjectSmall, ProjectTeamMember} from '@/features/projects/types'
import {AccessType} from '@/features/access-types/types'
import {AccessFrequency} from '@/features/planned-accesses/types'
import {Access} from '@/features/accesses/types'
import {MedicalReportType, MedicalReportTypeKeyName, medicalReportTypes, UploadedDocument} from '@/types'

export const REPORT_TEXTAREA_LIMIT = 10000

// All medical report dynamic fields
export const MedicalReportFieldsSchema = z.object({
    reportDate: z.date({required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:invalid_date')}),
    endDate: z
        .date({required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:invalid_date')})
        .nullish(),
    medicalRecordId: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')}),
    reportType: z.object(MedicalReportType.shape, {required_error: i18n.t('errors:required')}),
    projectId: z.string().nullish(), // used only for planned accesses purposes (not used for medical reports different from extension request)
    project: ProjectSmall.nullish(),
    status: z.enum(['draft', 'signed']),
    reportTypeKeyName: MedicalReportTypeKeyName,
    diagnosis: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    clinicalUpdate: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    remotePathologicalHistory: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    familyHistory: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    physiologicalHistory: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    physicalExam: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    socioFactors: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    conclusions: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    notes: z.string({required_error: i18n.t('errors:required')}).max(REPORT_TEXTAREA_LIMIT),
    treatmentsPlan: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    objectives: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .max(REPORT_TEXTAREA_LIMIT),
    cyclePeriodicityHypothesis: z.enum(CyclePeriodicityHypothesis.options, {
        required_error: i18n.t('errors:required'),
        invalid_type_error: i18n.t('errors:required')
    }),
    closureReason: z.enum(
        [
            'achievement_of_objectives',
            'patient_decision',
            'transition_to_territorial_services',
            'transfer_to_another_center_or_facility',
            'transfer_to_city_region_or_another_state',
            'therapist_shortage',
            'organizational_or_scheduling_difficulties',
            'lack_of_authorization_from_asl',
            'patient_death',
            'other'
        ],
        {required_error: i18n.t('errors:required')}
    ),
    reopeningNextCycle: z.date({
        required_error: i18n.t('errors:required'),
        invalid_type_error: i18n.t('errors:invalid_date')
    }),
    newProjectStartDate: z.date({
        required_error: i18n.t('errors:required'),
        invalid_type_error: i18n.t('errors:invalid_date')
    }),
    newProjectDurationDays: z.number({required_error: i18n.t('errors:required')}).int(),
    diagnosisNotes: z.string().optional(),
    additionalPredefinedText1: z.enum(PredefinedText.options, {required_error: i18n.t('errors:required')}),
    isAdditionalPredefinedText2: z.boolean().default(false),
    additionalNotes: z.string().optional(),
    therapistEvaluation: z
        .object(MedicalReportSmall.shape, {
            required_error: i18n.t('errors:required')
        })
        .or(z.string().optional()),
    teamEvaluation: z
        .object(MedicalReportSmall.shape, {
            required_error: i18n.t('errors:required')
        })
        .or(z.string().optional()),
    medicalEvaluation: z
        .object(MedicalReportSmall.shape, {
            required_error: i18n.t('errors:required')
        })
        .or(z.string().optional()),
    qualification: z.enum(MedicalReportQualification.options, {required_error: i18n.t('errors:required')}),
    workingCondition: z.enum(MedicalReportWorkingCondition.options, {required_error: i18n.t('errors:required')}),
    jobType: z.enum(MedicalReportJobType.options, {required_error: i18n.t('errors:required')}),
    civilDisability: z.coerce.boolean().default(false),
    companionAllowance: z.coerce.boolean().default(false),
    attendanceAllowance: z.coerce.boolean().default(false),
    law104Article3Paragraph1: z.coerce.boolean().default(false),
    law104Article3Paragraph3: z.coerce.boolean().default(false),
    disabilityPercentage: z
        .string({required_error: i18n.t('errors:required')})
        .transform(val => (val ? parseFloat(val) : undefined))
        .optional(),
    companionAllowanceReviewable: z.coerce.boolean().default(false),
    attendanceAllowanceReviewable: z.coerce.boolean().default(false),
    houseType: z.enum(MedicalReportHouseType.options, {required_error: i18n.t('errors:required')}),
    houseTypeOther: z.string().optional(),
    hasArchitecturalBarriers: z.coerce.boolean().default(false),
    architecturalBarriers: z.string().optional(),
    serviceDislocation: z.string({required_error: i18n.t('errors:required')}),
    otherHousingNotes: z.string().optional(),
    referenceContactFirstName: z.string({required_error: i18n.t('errors:required')}),
    referenceContactLastName: z.string({required_error: i18n.t('errors:required')}),
    referenceKinship: z.string({required_error: i18n.t('errors:required')}),
    referencePhone: z.string().optional(),
    familyMembersSituations: z
        .object({
            familyMemberFullName: z.string({required_error: i18n.t('errors:required')}),
            familyMemberAge: z.string({required_error: i18n.t('errors:required')}),
            familyMemberKinship: z.string({required_error: i18n.t('errors:required')}),
            familyMemberWorkingSituation: z.string({required_error: i18n.t('errors:required')})
        })
        .array()
        .optional(),
    intraFamilyCommunicationDifficulty: z.boolean().default(false),
    extraFamilyCommunicationDifficulty: z.boolean().default(false),
    disabilityExperienceProcessing: z.enum(DisabilityExperienceProcessing.options, {
        required_error: i18n.t('errors:required')
    }),
    volunteerNetwork: z.boolean().default(false),
    volunteerNetworkDetails: z.string().optional(),
    paidNetwork: z.boolean().default(false),
    paidNetworkDetails: z.string().optional(),
    activatedTerritorialServices: z.boolean().default(false),
    municipalTerritorialServices: z.string().optional(),
    aslTerritorialServices: z.string().optional(),
    homeCare: z.boolean().default(false),
    homeCareNotes: z.string().optional(),
    transport: z.boolean().default(false),
    transportNotes: z.string().optional(),
    dayCenters: z.coerce.boolean().default(false),
    dayCentersNotes: z.string().optional(),
    associations: z.coerce.boolean().default(false),
    associationsNotes: z.string().optional(),
    otherServices: z.coerce.boolean().default(false),
    otherServicesNotes: z.string().optional(),
    hobbiesNotes: z.string().optional(),
    schoolType: z.string().optional(),
    schoolName: z.string().optional(),
    hasSupportTeacher: z.coerce.boolean().default(false),
    supportTeacherHours: z.number().optional(),
    hasOepa: z.coerce.boolean().default(false),
    oepaHours: z.number().optional(),
    hasCommunicationAssistant: z.coerce.boolean().default(false),
    inclusionLevel: z.string().optional(),
    familySituationMinors: z.enum(FamilySituation.options).optional(),
    plannedAccesses: z.object({
        inPersonAccesses: z.array(
            z.object({
                accessType: AccessType.optional(),
                numberOfAccesses: z
                    .number()
                    .int()
                    .positive({message: i18n.t('errors:positive_number')})
                    .or(z.literal(''))
                    .optional(),
                frequency: AccessFrequency
            })
        ),
        singleAccesses: z.array(
            z.object({
                accessType: AccessType.optional(),
                numberOfAccesses: z
                    .number()
                    .int()
                    .positive({message: i18n.t('errors:positive_number')})
                    .or(z.literal(''))
                    .optional()
            })
        ),
        absenceAccesses: z.array(
            z.object({
                accessType: AccessType.optional(),
                numberOfAccesses: z.number().int().or(z.literal('')).optional()
            })
        )
    }),
    accesses: z.array(Access).optional(),
    isReportAccessDetatched: z.boolean(),
    teamMembers: z.array(ProjectTeamMember),
    documentTitle: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')}),
    documentDate: z.date({
        required_error: i18n.t('errors:required'),
        invalid_type_error: i18n.t('errors:invalid_date')
    }),
    documentAttachment: UploadedDocument,
    noProject: z.boolean().default(false)
})
export type MedicalReportFieldsSchema = z.infer<typeof MedicalReportFieldsSchema>

// Start continuous project schema
const StartContinuousProjectSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('start_continuous_project')}).merge(
        MedicalReportFieldsSchema.pick({
            diagnosis: true,
            clinicalUpdate: true,
            remotePathologicalHistory: true,
            familyHistory: true,
            physiologicalHistory: true,
            physicalExam: true,
            socioFactors: true,
            conclusions: true,
            treatmentsPlan: true,
            objectives: true
        })
    )
])

// Close continuous project schema
const CloseContinuousProjectSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('close_continuous_project')}).merge(
        MedicalReportFieldsSchema.pick({
            diagnosis: true,
            clinicalUpdate: true,
            physicalExam: true,
            socioFactors: true,
            conclusions: true,
            closureReason: true,
            endDate: true,
            notes: true
        })
    )
])

// Renew project validation schema
const RenewContinuousProjectSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('renew_continuous_project')}).merge(
        MedicalReportFieldsSchema.pick({
            diagnosis: true,
            clinicalUpdate: true,
            physicalExam: true,
            socioFactors: true,
            conclusions: true,
            treatmentsPlan: true,
            objectives: true
        })
    )
])

// Start cyclical project schema
const StartCyclicalProjectSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('start_cyclical_project')}).merge(
        MedicalReportFieldsSchema.pick({
            diagnosis: true,
            clinicalUpdate: true,
            pathologicalHistory: true, // todo: remove this attribute - used only for old tests
            remotePathologicalHistory: true,
            familyHistory: true,
            physiologicalHistory: true,
            physicalExam: true,
            socioFactors: true,
            conclusions: true,
            treatmentsPlan: true,
            objectives: true,
            cyclePeriodicityHypothesis: true
        })
    )
])

// Close cyclical project schema
const CloseCyclicalProjectSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('close_cyclical_project')}).merge(
        MedicalReportFieldsSchema.pick({
            diagnosis: true,
            clinicalUpdate: true,
            physicalExam: true,
            socioFactors: true,
            conclusions: true,
            reopeningNextCycle: true,
            cyclePeriodicityHypothesis: true,
            endDate: true
        })
    )
])

// Request of an extension for a project
const ProjectExtensionRequestSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('continuous_project_extension_request')}).merge(
        MedicalReportFieldsSchema.pick({
            newProjectStartDate: true,
            newProjectDurationDays: true,
            diagnosisNotes: true,
            additionalPredefinedText1: true,
            isAdditionalPredefinedText2: true,
            additionalNotes: true,
            therapistEvaluation: true,
            teamEvaluation: true,
            medicalEvaluation: true,
            plannedAccesses: true
        })
    )
])

// Initial social service evaluation schema
export const InitialSocialEvaluationSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('initial_social_service_evaluation')}).merge(
        MedicalReportFieldsSchema.pick({
            // Qualification and job fields
            qualification: true,
            workingCondition: true,
            jobType: true,
            // Disability benefits fields
            civilDisability: true,
            companionAllowance: true,
            attendanceAllowance: true,
            law104Article3Paragraph1: true,
            law104Article3Paragraph3: true,
            disabilityPercentage: true,
            companionAllowanceReviewable: true,
            attendanceAllowanceReviewable: true,
            // Housing condition fields
            houseType: true,
            houseTypeOther: true,
            hasArchitecturalBarriers: true,
            architecturalBarriers: true,
            serviceDislocation: true,
            otherHousingNotes: true,
            // Family network fields - Reference contact
            referenceContactFirstName: true,
            referenceContactLastName: true,
            referenceKinship: true,
            referencePhone: true,
            // Family situation fields
            familyMembersSituations: true,
            // Family atmosphere fields
            intraFamilyCommunicationDifficulty: true,
            extraFamilyCommunicationDifficulty: true,
            disabilityExperienceProcessing: true,
            // Care network fields
            volunteerNetwork: true,
            volunteerNetworkDetails: true,
            paidNetwork: true,
            paidNetworkDetails: true,
            activatedTerritorialServices: true,
            municipalTerritorialServices: true,
            aslTerritorialServices: true,
            homeCare: true,
            homeCareNotes: true,
            transport: true,
            transportNotes: true,
            dayCenters: true,
            dayCentersNotes: true,
            associations: true,
            associationsNotes: true,
            otherServices: true,
            otherServicesNotes: true,
            // Interests sections
            hobbiesNotes: true,
            schoolType: true,
            schoolName: true,
            hasSupportTeacher: true,
            supportTeacherHours: true,
            hasOepa: true,
            oepaHours: true,
            hasCommunicationAssistant: true,
            inclusionLevel: true,
            familySituationMinors: true
        })
    )
])

// Medical report notes schemas
const complexMedicalReportTypes = [
    'start_continuous_project',
    'close_continuous_project',
    'renew_continuous_project',
    'start_cyclical_project',
    'close_cyclical_project',
    'continuous_project_extension_request',
    'initial_social_service_evaluation',
    'team_report',
    'glo',
    'document'
]
const noteMedicalReportTypes = medicalReportTypes.filter(type => !complexMedicalReportTypes.includes(type))
const NotesMedicalReportSchemas = noteMedicalReportTypes.map(type =>
    z.discriminatedUnion('reportTypeKeyName', [
        z.object({reportTypeKeyName: z.literal(type)}).merge(
            MedicalReportFieldsSchema.pick({
                notes: true
            })
        )
    ])
)

// Medical report notes schemas
const teamReportGloTypes = ['team_report', 'glo']
const TeamReportGloSchemas = teamReportGloTypes.map(type =>
    z.discriminatedUnion('reportTypeKeyName', [
        z.object({reportTypeKeyName: z.literal(type)}).merge(
            MedicalReportFieldsSchema.pick({
                notes: true,
                teamMembers: true
            })
        )
    ])
)

// Document schema
const DocumentSchema = z.discriminatedUnion('reportTypeKeyName', [
    z.object({reportTypeKeyName: z.literal('document')}).merge(
        MedicalReportFieldsSchema.pick({
            documentTitle: true,
            documentDate: true,
            documentAttachment: true,
            notes: true
        })
    )
])

// Medical report validation schema
export const MedicalReportValidationSchema = MedicalReportFieldsSchema.pick({
    reportDate: true,
    medicalRecordId: true,
    reportType: true,
    status: true,
    reportTypeKeyName: true,
    project: true,
    endDate: true,
    accesses: true,
    isReportAccessDetatched: true,
    noProject: true
})
    .and(
        z.discriminatedUnion('reportTypeKeyName', [
            ...StartContinuousProjectSchema.options,
            ...CloseContinuousProjectSchema.options,
            ...RenewContinuousProjectSchema.options,
            ...StartCyclicalProjectSchema.options,
            ...CloseCyclicalProjectSchema.options,
            ...ProjectExtensionRequestSchema.options,
            ...InitialSocialEvaluationSchema.options,
            ...NotesMedicalReportSchemas.flatMap(schema => schema.options),
            ...TeamReportGloSchemas.flatMap(schema => schema.options),
            ...DocumentSchema.options
        ])
    )
    .superRefine((data, ctx) => {
        if (!data.isReportAccessDetatched && !data.accesses) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['accesses']
            })
        }
    })
export type MedicalReportValidationSchema = z.infer<typeof MedicalReportValidationSchema>

// Grouped schemas
export const StartProjectReportsSchema = MedicalReportFieldsSchema.pick({
    diagnosis: true,
    clinicalUpdate: true,
    remotePathologicalHistory: true,
    familyHistory: true,
    physiologicalHistory: true,
    physicalExam: true,
    socioFactors: true,
    conclusions: true
})
export type StartProjectReportsSchema = z.infer<typeof StartProjectReportsSchema>

export const StartProjectRehabilitationSchema = MedicalReportFieldsSchema.pick({
    treatmentsPlan: true,
    objectives: true
})
export type StartProjectRehabilitationSchema = z.infer<typeof StartProjectRehabilitationSchema>

export const CloseProjectReportsSchema = MedicalReportFieldsSchema.pick({
    diagnosis: true,
    clinicalUpdate: true,
    physicalExam: true,
    socioFactors: true,
    conclusions: true
})
export type CloseProjectReportsSchema = z.infer<typeof CloseProjectReportsSchema>

export const RenewProjectReportsSchema = MedicalReportFieldsSchema.pick({
    diagnosis: true,
    clinicalUpdate: true,
    physicalExam: true,
    socioFactors: true,
    conclusions: true
})
export type RenewProjectReportsSchema = z.infer<typeof RenewProjectReportsSchema>

export const RenewProjectRehabilitationSchema = MedicalReportFieldsSchema.pick({
    treatmentsPlan: true,
    objectives: true
})
export type RenewProjectRehabilitationSchema = z.infer<typeof RenewProjectRehabilitationSchema>

export const AttachedClinicalReportSchema = MedicalReportFieldsSchema.pick({
    project: true,
    therapistEvaluation: true,
    teamEvaluation: true,
    medicalEvaluation: true
})
export type AttachedClinicalReportSchema = z.infer<typeof AttachedClinicalReportSchema>

export const MedicalReportActivitiesInPresenceSchema = MedicalReportFieldsSchema.pick({
    plannedAccesses: true
})
export type MedicalReportActivitiesInPresenceSchema = z.infer<typeof MedicalReportActivitiesInPresenceSchema>

export const MedicalReportAttributesSchema = MedicalReportFieldsSchema.omit({
    reportDate: true,
    endDate: true,
    medicalRecordId: true,
    reportType: true,
    projectId: true,
    project: true,
    status: true,
    reportTypeKeyName: true,
    accesses: true
})
export type MedicalReportAttributesSchema = z.infer<typeof MedicalReportAttributesSchema>

export const HousingNotesSchema = MedicalReportFieldsSchema.pick({
    serviceDislocation: true,
    otherHousingNotes: true
})
export type HousingNotesSchema = z.infer<typeof HousingNotesSchema>

export const FamilyAtmosphereFieldsSchema = MedicalReportFieldsSchema.pick({
    intraFamilyCommunicationDifficulty: true,
    extraFamilyCommunicationDifficulty: true,
    disabilityExperienceProcessing: true
})
export type FamilyAtmosphereFieldsSchema = z.infer<typeof FamilyAtmosphereFieldsSchema>

export const CareNetworkFieldsSchema = MedicalReportFieldsSchema.pick({
    volunteerNetwork: true,
    volunteerNetworkDetails: true,
    paidNetwork: true,
    paidNetworkDetails: true,
    activatedTerritorialServices: true,
    municipalTerritorialServices: true,
    aslTerritorialServices: true,
    homeCare: true,
    homeCareNotes: true,
    transport: true,
    transportNotes: true,
    dayCenters: true,
    dayCentersNotes: true,
    associations: true,
    associationsNotes: true,
    otherServices: true,
    otherServicesNotes: true
})
export type CareNetworkFieldsSchema = z.infer<typeof CareNetworkFieldsSchema>
